import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  IconButton,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import AppLayout from '../components/AppLayout';
import TeamRosterHelpSidebar from '../components/TeamRosterHelpSidebar';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { User, EmployeeCheckInConfig, CheckInFrequency, ManagerCheckInPreference } from '../types';
import { RoleTemplateState } from '../types';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { getRoleBadgeColor } from '../utils/roleBadge';
import { formatDate, formatCalendarDateIso, parseUtcDate } from '../utils/dateFormat';
import { AppDatePicker } from '../components/AppDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { createShouldDisableDate } from '../utils/dateValidation';
import { useDateLocale } from '../hooks/useDateLocale';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, couldNotUpdate, couldNotAcknowledge } from '../utils/userFacingCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';
import { acknowledgedSuccessfully, successCopy } from '../utils/successCopy';
import { checkInOptOutAcknowledgmentPoints, checkInFrequencyConfirmCopy } from '../utils/checkInOptOutCopy';

interface RoleTemplate {
  id: string;
  name: string;
  description?: string;
  state?: RoleTemplateState;
  lastEditedBy?: string;
  lastEditedAt?: string;
  lastApprovedBy?: string;
  lastApprovedAt?: string;
}

export default function TeamRosterPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const showSuccess = useSuccessSnackbar();
  const country = useDateLocale();
  const { isHoliday } = usePublicHolidays(country);
  const shouldDisableDate = createShouldDisableDate(isHoliday);

  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [checkInConfigs, setCheckInConfigs] = useState<EmployeeCheckInConfig[]>([]);
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'name' | 'email' | 'role' | 'roleTemplate' | 'checkInFrequency' | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [savingEmployeeId, setSavingEmployeeId] = useState<string | null>(null);
  const [savingRoleTemplateEmployeeId, setSavingRoleTemplateEmployeeId] = useState<string | null>(null);
  const [helpExpanded, setHelpExpanded] = useState(false);

  usePageAgentContext({
    pageId: 'team-roster',
    pageTitle: 'Team Roster',
    visibleSections: ['team-members', 'built-in-check-ins', 'role-template'],
    suggestedPrompts: [
      'How do I set check-in frequency for my team?',
      'How do I turn off built-in check-ins?',
      'What happens when I opt out of built-in check-ins?',
    ],
  });

  // Team member edit modal (role template + check-in schedule)
  const [checkInConfigModalEmployee, setCheckInConfigModalEmployee] = useState<User | null>(null);
  const [modalFrequency, setModalFrequency] = useState<CheckInFrequency | null>(null);
  const [modalFirstDate, setModalFirstDate] = useState<Date | null>(null);
  const [checkInPreference, setCheckInPreference] = useState<ManagerCheckInPreference | null>(null);
  const [optOutDialogOpen, setOptOutDialogOpen] = useState(false);
  const [optOutAckChecked, setOptOutAckChecked] = useState(false);
  const [preferenceSaving, setPreferenceSaving] = useState(false);

  const loadCheckInPreference = useCallback(async () => {
    try {
      const response = await apiService.get<ManagerCheckInPreference>(API_ENDPOINTS.CHECK_INS.MANAGER_PREFERENCE);
      setCheckInPreference(response.data);
    } catch {
      setCheckInPreference(null);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError('');

        // Load direct reports
        const teamResponse = await apiService.get<User[]>(API_ENDPOINTS.USERS.MY_TEAM);
        setTeamMembers(teamResponse.data);

        // Load role templates for role template selection
        try {
          const roleTemplatesResponse = await apiService.get<RoleTemplate[]>(API_ENDPOINTS.ROLES.BASE);
          setRoleTemplates(roleTemplatesResponse.data);
        } catch (err) {
          console.warn('Failed to load role templates:', err);
          setRoleTemplates([]);
        }

        // Load check-in configurations
        try {
          const configsResponse = await apiService.get<EmployeeCheckInConfig[]>(API_ENDPOINTS.CHECK_INS.CONFIG_TEAM);
          setCheckInConfigs(configsResponse.data);
        } catch (err) {
          // Not all managers may have check-in configs set up yet
          console.warn('Failed to load check-in configurations:', err);
          setCheckInConfigs([]);
        }

        await loadCheckInPreference();
      } catch (err: unknown) {
                setError(getApiErrorMessage(err, couldNotLoad('team data')));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, loadCheckInPreference]);

  const usesBuiltInCheckIns = checkInPreference?.usesBuiltInCheckIns !== false;

  const handleOptOutConfirm = async () => {
    if (!optOutAckChecked || preferenceSaving) return;
    setPreferenceSaving(true);
    setError('');
    try {
      await apiService.put(API_ENDPOINTS.CHECK_INS.MANAGER_PREFERENCE_OPT_OUT, {
        acknowledged: true,
      });
      await loadCheckInPreference();
      const configsResponse = await apiService.get<EmployeeCheckInConfig[]>(API_ENDPOINTS.CHECK_INS.CONFIG_TEAM);
      setCheckInConfigs(configsResponse.data);
      setOptOutDialogOpen(false);
      setOptOutAckChecked(false);
      showSuccess(successCopy.checkInsOptedOut);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotUpdate('your check-in preference')));
    } finally {
      setPreferenceSaving(false);
    }
  };

  const handleOptIn = async () => {
    if (preferenceSaving) return;
    setPreferenceSaving(true);
    setError('');
    try {
      await apiService.put(API_ENDPOINTS.CHECK_INS.MANAGER_PREFERENCE_OPT_IN);
      await loadCheckInPreference();
      showSuccess(successCopy.checkInsOptedIn);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotUpdate('your check-in preference')));
    } finally {
      setPreferenceSaving(false);
    }
  };
  /** Default first check-in due date when manager sets a frequency (required by API). */
  const getDefaultFirstCheckInDate = (frequency: CheckInFrequency): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = today.getDay(); // 0 = Sunday
    const daysToMonday = day === 0 ? 1 : day === 1 ? 0 : 8 - day;
    if (frequency === 'Weekly') {
      const d = new Date(today);
      d.setDate(d.getDate() + daysToMonday);
      return d;
    }
    if (frequency === 'Fortnightly') {
      const d = new Date(today);
      d.setDate(d.getDate() + daysToMonday + 14);
      return d;
    }
    // Monthly: first day of next month
    const d = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return d;
  };

  const handleCheckInFrequencyChange = async (
    employeeId: string,
    frequency: CheckInFrequency | null,
    firstCheckInDueDate?: Date | null
  ) => {
    setSavingEmployeeId(employeeId);
    setError('');
    try {
      const payload: { frequency: CheckInFrequency | null; firstCheckInDueDate?: string } = {
        frequency: frequency || null,
      };
      if (frequency) {
        // First check-in date is required when setting a frequency
        const dateToSend = firstCheckInDueDate ?? getDefaultFirstCheckInDate(frequency);
        payload.firstCheckInDueDate = formatCalendarDateIso(dateToSend);
      }
      await apiService.put(API_ENDPOINTS.CHECK_INS.CONFIG_BY_EMPLOYEE(employeeId), payload);
      // Reload configs
      const configsResponse = await apiService.get<EmployeeCheckInConfig[]>(API_ENDPOINTS.CHECK_INS.CONFIG_TEAM);
      setCheckInConfigs(configsResponse.data);

      // Show success message
      const employee = teamMembers.find(m => m.id === employeeId);
      const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Team member';
      const frequencyText = frequency === 'Weekly' ? 'Weekly' : frequency === 'Fortnightly' ? 'Fortnightly' : frequency === 'Monthly' ? 'Monthly' : 'None';
      const dateStr = payload.firstCheckInDueDate ? formatDate(payload.firstCheckInDueDate) : '';
      showSuccess(
        frequency
          ? successCopy.checkInScheduleSet(employeeName, frequencyText, dateStr)
          : successCopy.checkInDisabled(employeeName),
      );
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotUpdate('check-in frequency')));
    } finally {
      setSavingEmployeeId(null);
    }
  };

  const handleAcknowledgeRoleTemplateChangeAsManager = async (employeeId: string) => {
    if (!user) return;

    setSavingRoleTemplateEmployeeId(employeeId);
    setError('');
    try {
      await apiService.post(API_ENDPOINTS.USERS.ACKNOWLEDGE_ROLE_TEMPLATE_CHANGE_AS_MANAGER(employeeId));

      // Reload team members to get updated status
      const teamResponse = await apiService.get<User[]>(API_ENDPOINTS.USERS.MY_TEAM);
      setTeamMembers(teamResponse.data);

      // Show success message
      const employee = teamMembers.find(m => m.id === employeeId);
      const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : 'Team member';
      showSuccess(acknowledgedSuccessfully(`Role template change for ${employeeName}`));
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotAcknowledge('the role template change')));
    } finally {
      setSavingRoleTemplateEmployeeId(null);
    }
  };

  const getCheckInFrequencyForUser = useCallback((userId: string): CheckInFrequency | null => {
    const config = checkInConfigs.find(c => c.employeeId === userId);
    if (!config?.isActive) return null;

    // Convert numeric enum value to string if needed
    const freq = config.frequency;
    if (typeof freq === 'number') {
      const freqMap: Record<number, CheckInFrequency> = {
        1: 'Weekly',
        2: 'Fortnightly',
        3: 'Monthly',
      };
      return freqMap[freq] || null;
    }
    return freq as CheckInFrequency;
  }, [checkInConfigs]);

  const openCheckInConfigModal = (member: User) => {
    const freq = getCheckInFrequencyForUser(member.id);
    const config = checkInConfigs.find(c => c.employeeId === member.id && c.isActive);
    const existingDate = parseUtcDate(config?.nextCheckInDueDate);
    setCheckInConfigModalEmployee(member);
    setModalFrequency(freq);
    setModalFirstDate(existingDate ?? (freq ? getDefaultFirstCheckInDate(freq) : null));
  };

  const closeCheckInConfigModal = () => {
    setCheckInConfigModalEmployee(null);
    setModalFrequency(null);
    setModalFirstDate(null);
  };

  const modalHasPendingRoleTemplateChange = (): boolean => {
    if (!checkInConfigModalEmployee) return false;
    const pending = checkInConfigModalEmployee.pendingRoleTemplateId
      ? roleTemplates.find(rt => rt.id === checkInConfigModalEmployee!.pendingRoleTemplateId)
      : null;
    const stateIsApproved = pending?.state === RoleTemplateState.Approved || pending?.state === 'Approved' || pending?.state === 3;
    return !!(checkInConfigModalEmployee.pendingRoleTemplateId && stateIsApproved);
  };

  const handleCheckInConfigModalSave = async () => {
    if (!checkInConfigModalEmployee) return;
    const member = checkInConfigModalEmployee;
    const pendingNeedsAck = modalHasPendingRoleTemplateChange() && !member.roleTemplateChangeManagerAcknowledgedAt;

    // Role template changes are admin-only; managers can only acknowledge pending changes
    if (pendingNeedsAck) {
      await handleAcknowledgeRoleTemplateChangeAsManager(member.id);
    }

    const freq = modalFrequency;
    const firstDate = freq ? (modalFirstDate ?? getDefaultFirstCheckInDate(freq)) : undefined;
    await handleCheckInFrequencyChange(member.id, freq ?? null, firstDate);

    closeCheckInConfigModal();
  };

  const filteredTeamMembers = useMemo(() => {
    if (!searchQuery.trim()) return teamMembers;

    const query = searchQuery.toLowerCase();
    return teamMembers.filter(member =>
      `${member.firstName} ${member.lastName}`.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query) ||
      member.roles.some(role => role.toLowerCase().includes(query))
    );
  }, [teamMembers, searchQuery]);

  const handleSort = (field: 'name' | 'email' | 'role' | 'roleTemplate' | 'checkInFrequency') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'email' | 'role' | 'roleTemplate' | 'checkInFrequency') => {
    if (sortField !== field) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    }
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const sortedTeamMembers = useMemo(() => {
    if (!sortField) {
      return filteredTeamMembers;
    }

    return [...filteredTeamMembers].sort((a, b) => {
      let aValue: string | number | null;
      let bValue: string | number | null;

      if (sortField === 'name') {
        aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
        bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === 'email') {
        aValue = a.email.toLowerCase();
        bValue = b.email.toLowerCase();
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === 'role') {
        // Sort by first role alphabetically, or by number of roles
        aValue = a.roles.length > 0 ? a.roles.sort()[0].toLowerCase() : '';
        bValue = b.roles.length > 0 ? b.roles.sort()[0].toLowerCase() : '';
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === 'roleTemplate') {
        const aTemplate = a.roleTemplateId
          ? roleTemplates.find(rt => rt.id === a.roleTemplateId)
          : null;
        const bTemplate = b.roleTemplateId
          ? roleTemplates.find(rt => rt.id === b.roleTemplateId)
          : null;
        aValue = aTemplate?.name || '';
        bValue = bTemplate?.name || '';
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (sortField === 'checkInFrequency') {
        // Sort by frequency: Weekly < Fortnightly < Monthly < null
        const frequencyOrder: Record<string, number> = {
          'Weekly': 1,
          'Fortnightly': 2,
          'Monthly': 3,
        };
        aValue = getCheckInFrequencyForUser(a.id);
        bValue = getCheckInFrequencyForUser(b.id);
        const aOrder = aValue ? frequencyOrder[aValue] || 999 : 999;
        const bOrder = bValue ? frequencyOrder[bValue] || 999 : 999;
        return sortDirection === 'asc' ? aOrder - bOrder : bOrder - aOrder;
      }

      return 0;
    });
  }, [filteredTeamMembers, sortField, sortDirection, roleTemplates, getCheckInFrequencyForUser]);

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error && !teamMembers.length) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <PeopleIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Team Roster
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {usesBuiltInCheckIns
              ? checkInFrequencyConfirmCopy.rosterWhenBuiltInOn
              : 'Built-in check-ins are turned off for your team. You manage ongoing 1:1s off-system. Formal reviews and development plans in Aardvark are unchanged.'}
          </Typography>
        </Alert>

        {checkInPreference?.canManagePreference && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  alignItems: { xs: 'stretch', sm: 'flex-start' },
                  gap: 2,
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Built-in check-ins
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {usesBuiltInCheckIns
                      ? checkInFrequencyConfirmCopy.rosterBuiltInCardWhenOn
                      : 'You have turned off built-in check-ins. Turn them back on when you want Aardvark to schedule check-ins again.'}
                  </Typography>
                </Box>
                {usesBuiltInCheckIns ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={preferenceSaving}
                    sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                    onClick={() => {
                      setOptOutAckChecked(false);
                      setOptOutDialogOpen(true);
                    }}
                  >
                    Turn off built-in check-ins
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    disabled={preferenceSaving}
                    sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                    onClick={handleOptIn}
                  >
                    {preferenceSaving ? 'Saving…' : 'Turn built-in check-ins back on'}
                  </Button>
                )}
              </Box>
              {!usesBuiltInCheckIns && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.100',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    What you confirmed
                  </Typography>
                  <List dense disablePadding>
                    {(checkInPreference.acknowledgmentPoints?.length
                      ? checkInPreference.acknowledgmentPoints
                      : checkInOptOutAcknowledgmentPoints
                    ).map((point) => (
                      <ListItem key={point} sx={{ alignItems: 'flex-start', py: 0.5, px: 0 }}>
                        <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                  {checkInPreference.checkInsOptOutAcknowledgedAt && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                      You confirmed this on{' '}
                      {formatDate(checkInPreference.checkInsOptOutAcknowledgedAt, { includeTime: true })}.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              label="Search team members"
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {searchQuery.trim() !== '' && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Chip
                  label={`Search: "${searchQuery}"`}
                  onDelete={() => setSearchQuery('')}
                  size="small"
                  color="primary"
                />
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>

            {filteredTeamMembers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchQuery ? 'No team members match your search or filters.' : 'No team members yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Team members will appear here once they are assigned to you as their manager'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('name')}
                        >
                          <strong>Name</strong>
                          {getSortIcon('name')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('email')}
                        >
                          <strong>Email</strong>
                          {getSortIcon('email')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('role')}
                        >
                          <strong>Role</strong>
                          {getSortIcon('role')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('roleTemplate')}
                        >
                          <strong>Role Template</strong>
                          {getSortIcon('roleTemplate')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.7 },
                          }}
                          onClick={() => handleSort('checkInFrequency')}
                        >
                          <strong>Check-In Frequency</strong>
                          {getSortIcon('checkInFrequency')}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <strong>Next check-in date</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTeamMembers.map((member) => (
                      <TableRow key={member.id} hover>
                        <TableCell>
                          {member.firstName} {member.lastName}
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          {member.roles.map((role) => (
                            <Chip
                              key={role}
                              label={role}
                              size="small"
                              color={getRoleBadgeColor(role)}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const isEmployee = !member.roles.some(role =>
                              role === 'Manager' || role === 'SeniorManager' || role === 'Administrator'
                            );

                            if (!isEmployee) {
                              const currentTemplate = member.roleTemplateId
                                ? roleTemplates.find(rt => rt.id === member.roleTemplateId)
                                : null;
                              return (
                                <Box>
                                  {currentTemplate ? (
                                    <Typography variant="body2" fontWeight="500">
                                      {currentTemplate.name}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      Not assigned
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Contact administrator to change
                                  </Typography>
                                </Box>
                              );
                            }

                            // Check if there's a pending role template change
                            // Check RoleTemplate.State instead of user fields
                            const pendingTemplate = member.pendingRoleTemplateId
                              ? roleTemplates.find(rt => rt.id === member.pendingRoleTemplateId)
                              : null;
                            const currentTemplate = member.roleTemplateId
                              ? roleTemplates.find(rt => rt.id === member.roleTemplateId)
                              : null;

                            const stateIsApproved = pendingTemplate?.state === RoleTemplateState.Approved ||
                                                   pendingTemplate?.state === 'Approved' ||
                                                   pendingTemplate?.state === 3;

                            const hasPendingChange = member.pendingRoleTemplateId && stateIsApproved;

                            if (hasPendingChange) {
                              const managerAcknowledged = !!member.roleTemplateChangeManagerAcknowledgedAt;
                              const employeeAcknowledged = !!member.roleTemplateChangeEmployeeAcknowledgedAt;
                              return (
                                <Box>
                                  <Typography variant="body2" fontWeight="500">
                                    {currentTemplate?.name || 'Not assigned'} → {pendingTemplate?.name || 'Unknown'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {employeeAcknowledged
                                      ? 'Change completed'
                                      : managerAcknowledged
                                        ? 'Pending employee acknowledgment'
                                        : 'Pending your acknowledgment'}
                                  </Typography>
                                </Box>
                              );
                            }

                            return (
                              <Typography variant="body2" sx={!currentTemplate ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}>
                                {currentTemplate?.name || 'Not assigned'}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {!usesBuiltInCheckIns ? (
                            <Typography variant="body2" color="text.secondary">Off-system</Typography>
                          ) : getCheckInFrequencyForUser(member.id) ? (
                            <Typography variant="body2">{getCheckInFrequencyForUser(member.id)}</Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Not set</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {!usesBuiltInCheckIns ? (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          ) : getCheckInFrequencyForUser(member.id) ? (
                            (() => {
                              const config = checkInConfigs.find(c => c.employeeId === member.id && c.isActive);
                              const dateStr = config?.nextCheckInDueDate ? formatDate(config.nextCheckInDueDate) : '—';
                              return (
                                <Typography variant="body2">{dateStr}</Typography>
                              );
                            })()
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openCheckInConfigModal(member)}
                            disabled={savingEmployeeId === member.id}
                            aria-label="Edit team member"
                            title="Edit team member"
                          >
                            {savingEmployeeId === member.id ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <SettingsIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Team member edit modal (role template + check-in schedule) */}
        <Dialog open={!!checkInConfigModalEmployee} onClose={closeCheckInConfigModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            Team member
            {checkInConfigModalEmployee && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontWeight: 'normal' }}>
                {checkInConfigModalEmployee.firstName} {checkInConfigModalEmployee.lastName}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {/* Role template (read-only for managers; only administrators can change it) */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Role template</Typography>
            {checkInConfigModalEmployee && modalHasPendingRoleTemplateChange() ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {roleTemplates.find(rt => rt.id === checkInConfigModalEmployee.roleTemplateId)?.name || 'Not assigned'} → {roleTemplates.find(rt => rt.id === checkInConfigModalEmployee.pendingRoleTemplateId)?.name || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {checkInConfigModalEmployee.roleTemplateChangeManagerAcknowledgedAt
                    ? 'Pending employee acknowledgment'
                    : 'Pending your acknowledgment'}
                </Typography>
                {!checkInConfigModalEmployee.roleTemplateChangeManagerAcknowledgedAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Click Save to acknowledge.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2">
                  {checkInConfigModalEmployee
                    ? (roleTemplates.find(rt => rt.id === checkInConfigModalEmployee.roleTemplateId)?.name || 'Not assigned')
                    : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Only administrators can change role templates. Contact an administrator if you need a change.
                </Typography>
              </Box>
            )}

            {/* Check-in schedule */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Check-in schedule</Typography>
            {!usesBuiltInCheckIns ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                Built-in check-ins are turned off for your team. Turn them back on from the card above this table to set schedules again.
              </Alert>
            ) : (
              <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set how often this team member should have check-ins, and the due date for the first one. By saving, you confirm you&apos;ve agreed this with your report. The system will then prompt you at this frequency to record whether each check-in happened.
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={modalFrequency ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const freq = value === '' ? null : (value as CheckInFrequency);
                  setModalFrequency(freq);
                  setModalFirstDate(freq ? getDefaultFirstCheckInDate(freq) : null);
                }}
                label="Frequency"
              >
                <MenuItem value="">
                  <em>None (no scheduled check-ins)</em>
                </MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Fortnightly">Fortnightly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
            {modalFrequency && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <AppDatePicker
                  label="First check-in due date"
                  value={modalFirstDate}
                  onChange={(date) => setModalFirstDate(date)}
                  minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  shouldDisableDate={shouldDisableDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Choose the actual due date for the first check-in (e.g. next Monday or 1st of month).',
                    },
                  }}
                />
              </LocalizationProvider>
            )}
              </>
            )}

          </DialogContent>
          <DialogActions>
            <Button onClick={closeCheckInConfigModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCheckInConfigModalSave}
              disabled={
                savingEmployeeId === checkInConfigModalEmployee?.id ||
                savingRoleTemplateEmployeeId === checkInConfigModalEmployee?.id ||
                (!!modalFrequency && !modalFirstDate) ||
                (!usesBuiltInCheckIns && !modalHasPendingRoleTemplateChange())
              }
            >
              {(savingEmployeeId === checkInConfigModalEmployee?.id || savingRoleTemplateEmployeeId === checkInConfigModalEmployee?.id) ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Save'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={optOutDialogOpen}
          onClose={() => !preferenceSaving && setOptOutDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 2,
              ...(isMobile ? { display: 'flex', flexDirection: 'column', height: '100%' } : {}),
            },
          }}
        >
          <DialogTitle sx={{ flexShrink: 0 }}>Turn off built-in check-ins?</DialogTitle>
          <DialogContent sx={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Before turning off built-in check-ins, please confirm:
            </Typography>
            <List dense disablePadding>
              {(checkInPreference?.acknowledgmentPoints?.length
                ? checkInPreference.acknowledgmentPoints
                : checkInOptOutAcknowledgmentPoints
              ).map((point) => (
                <ListItem key={point} sx={{ alignItems: 'flex-start', py: 0.5, px: 0 }}>
                  <ListItemText primary={`• ${point}`} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
            <FormControlLabel
              sx={{ mt: 2, ml: 0, displayItems: 'center' }}
              control={
                <Checkbox
                  checked={optOutAckChecked}
                  onChange={(e) => setOptOutAckChecked(e.target.checked)}
                  disabled={preferenceSaving}
                />
              }
              label="I have read and confirm the above."
            />
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              flexShrink: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Button onClick={() => setOptOutDialogOpen(false)} disabled={preferenceSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleOptOutConfirm}
              disabled={!optOutAckChecked || preferenceSaving}
              sx={{
                '&.Mui-disabled': {
                  bgcolor: (theme) => theme.palette.warning.main,
                  color: (theme) => theme.palette.warning.contrastText,
                  opacity: 0.45,
                },
              }}
            >
              {preferenceSaving ? 'Saving…' : 'Turn off built-in check-ins'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      {showLegacyHelp && (
        <TeamRosterHelpSidebar
          isExpanded={helpExpanded}
          onToggle={() => setHelpExpanded(!helpExpanded)}
        />
      )}
    </AppLayout>
  );
}
